import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getCustomer } from '@/lib/api/customers.js';
import { inquiriesApi } from '@/lib/api/inquiries.js';
import { listDeviceMakes, listDeviceModels } from '@/lib/api/devices.js';
import { listRepairServices, type RepairService } from '@/lib/api/repair-services.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function serviceDefaultPrice(service: RepairService): string {
  if (service.resolvedPrice != null && service.resolvedPrice !== '') {
    return String(service.resolvedPrice);
  }
  return String(service.defaultPrice ?? '0');
}

type SelectedService = { service: RepairService; price: string };

export function NewInquiryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [makeId, setMakeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [customModelName, setCustomModelName] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [source, setSource] = useState<'phone' | 'walk_in' | 'online' | 'email'>('phone');
  const [notes, setNotes] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [selectedServices, setSelectedServices] = useState<Record<string, SelectedService>>({});

  useEffect(() => {
    const st = location.state as { customerId?: string } | undefined;
    const customerId = st?.customerId;
    if (!customerId) return;
    void getCustomer(customerId).then((customer) => {
      setCustomerName(`${customer.firstName} ${customer.lastName ?? ''}`.trim());
      setCustomerPhone(customer.phone ?? '');
      setCustomerEmail(customer.email ?? '');
    });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const makesQuery = useQuery({ queryKey: ['device-makes'], queryFn: listDeviceMakes });
  const modelsQuery = useQuery({
    queryKey: ['device-models', makeId],
    queryFn: () => listDeviceModels(makeId ? { makeId } : undefined),
    enabled: Boolean(makeId),
  });

  const matchingModels = modelsQuery.data ?? [];

  const servicesQuery = useQuery({
    queryKey: ['repair-services', makeId, modelId],
    queryFn: () =>
      listRepairServices({
        makeId: makeId || undefined,
        modelId: modelId || undefined,
        displayOnPos: true,
        requireModelPricing: Boolean(modelId),
      }),
    enabled: Boolean(modelId),
  });

  const filteredServices = useMemo(
    () =>
      (servicesQuery.data ?? []).filter((service) =>
        service.name.toLowerCase().includes(serviceSearch.trim().toLowerCase()),
      ),
    [serviceSearch, servicesQuery.data],
  );

  const quotedTotal = Object.values(selectedServices).reduce(
    (sum, entry) => sum + Number(entry.price || 0),
    0,
  );

  const createMut = useMutation({
    mutationFn: () =>
      inquiriesApi.create({
        customerName: customerName.trim() || undefined,
        customerPhone: customerPhone.trim() || undefined,
        customerEmail: customerEmail.trim() || undefined,
        deviceMakeId: makeId || undefined,
        deviceModelId: modelId || undefined,
        deviceModel: modelId ? undefined : customModelName.trim() || undefined,
        issueDescription: issueDescription.trim() || undefined,
        source,
        notes: notes.trim() || undefined,
        servicesQuoted: Object.values(selectedServices).map(({ service, price }) => ({
          serviceId: service.id,
          serviceName: service.name,
          price: Number(price),
        })),
      }),
    onSuccess: (res) => {
      toastSuccess(`Inquiry ${res.data.inquiryNumber} created`);
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['inquiries-stats'] });
      navigate(`/inquiries/${res.data.id}`);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function selectMake(nextMakeId: string) {
    setMakeId(nextMakeId);
    setModelId('');
    setCustomModelName('');
    setSelectedServices({});
    setServiceSearch('');
  }

  function selectModel(nextModelId: string) {
    setModelId(nextModelId);
    setCustomModelName('');
    setSelectedServices({});
    setServiceSearch('');
  }

  function toggleService(service: RepairService) {
    setSelectedServices((current) => {
      const next = { ...current };
      if (next[service.id]) {
        delete next[service.id];
      } else {
        next[service.id] = { service, price: serviceDefaultPrice(service) };
      }
      return next;
    });
  }

  function setServicePrice(serviceId: string, price: string) {
    setSelectedServices((current) => ({
      ...current,
      [serviceId]: {
        ...current[serviceId]!,
        price,
      },
    }));
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-950">New Inquiry</h1>
          <p className="text-sm text-slate-600">Capture a phone or walk-in quote</p>
        </div>
        <Link to="/inquiries" className="text-sm font-semibold text-primary">
          ← Back to list
        </Link>
      </header>

      <form
        className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          createMut.mutate();
        }}
      >
        <section className="grid gap-4 md:grid-cols-2">
          <label className="block md:col-span-2">
            <span className="text-sm font-medium">Customer name</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Phone</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Email</span>
            <input
              type="email"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Source</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={source}
              onChange={(e) => setSource(e.target.value as typeof source)}
            >
              <option value="phone">Phone</option>
              <option value="walk_in">Walk-in</option>
              <option value="online">Online</option>
              <option value="email">Email</option>
            </select>
          </label>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium">Device make</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={makeId}
              onChange={(e) => selectMake(e.target.value)}
            >
              <option value="">Select make</option>
              {(makesQuery.data ?? []).map((make) => (
                <option key={make.id} value={make.id}>
                  {make.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium">Device model</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 disabled:bg-slate-50"
              value={modelId}
              onChange={(e) => selectModel(e.target.value)}
              disabled={!makeId}
            >
              <option value="">Select model</option>
              {matchingModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))}
            </select>
            {makeId && matchingModels.length === 0 && !modelsQuery.isLoading ? (
              <input
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={customModelName}
                onChange={(e) => setCustomModelName(e.target.value)}
                placeholder="Model not in catalog"
              />
            ) : null}
          </label>
          <label className="block md:col-span-2">
            <span className="text-sm font-medium">Issue description</span>
            <textarea
              className="mt-1 min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
            />
          </label>
        </section>

        <section>
          <div className="mb-3">
            <h2 className="font-semibold text-slate-950">Services quoted</h2>
            <p className="text-sm text-slate-500">
              Select services from the repair catalog for this device.
            </p>
          </div>

          {!modelId ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Select a catalog device model to see repair services with pricing.
            </div>
          ) : null}

          {modelId ? (
            <>
              <input
                type="search"
                className="mb-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                placeholder="Search services…"
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
              />

              {servicesQuery.isLoading ? (
                <p className="text-sm text-slate-500">Loading services…</p>
              ) : null}

              {!servicesQuery.isLoading && filteredServices.length === 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  No repair services are configured for this device. Add pricing in Settings → Repair
                  Services.
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                {filteredServices.map((service) => {
                  const selected = selectedServices[service.id];
                  return (
                    <div
                      key={service.id}
                      className={[
                        'rounded-xl border p-4',
                        selected ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white',
                      ].join(' ')}
                    >
                      <button
                        type="button"
                        className="w-full text-left"
                        onClick={() => toggleService(service)}
                      >
                        <div className="font-semibold text-slate-950">{service.name}</div>
                        <div className="mt-1 text-sm font-semibold text-slate-700">
                          {formatCurrency(serviceDefaultPrice(service))}
                        </div>
                      </button>
                      {selected ? (
                        <label className="mt-3 block">
                          <span className="text-xs font-semibold text-slate-500">Quoted price $</span>
                          <input
                            type="number"
                            min={0}
                            step={0.01}
                            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                            value={selected.price}
                            onChange={(e) => setServicePrice(service.id, e.target.value)}
                          />
                        </label>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}

          <p className="mt-3 text-sm font-semibold text-slate-700">
            Total: {formatCurrency(quotedTotal)}
          </p>
        </section>

        <label className="block">
          <span className="text-sm font-medium">Internal notes</span>
          <textarea
            className="mt-1 min-h-16 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <button
          type="submit"
          disabled={createMut.isPending}
          className="rounded-xl bg-primary px-6 py-2 font-semibold text-white disabled:opacity-50"
        >
          {createMut.isPending ? 'Creating…' : 'Create Inquiry'}
        </button>
      </form>
    </div>
  );
}
